<template>
  <div class="pa-2">
    <system-health-banner
      title="Map API Workers"
      :subtitle="bannerSubtitle"
      :status="geoBackupStatus"
    />

    <div class="mb-2">
      <status-chip :status="geoBackupStatus" :label="geoBackupChipLabel" />
      <status-chip :status="routeGeocodeChipStatus" :label="routeGeocodeChipLabel" />
      <status-chip :status="syncChipStatus" :label="syncChipLabel" />
      <status-chip :status="auditChipStatus" :label="auditChipLabel" />
      <status-chip :status="distanceReportChipStatus" :label="distanceReportChipLabel" />
      <status-chip :status="cleanupChipStatus" :label="cleanupChipLabel" />
      <status-chip
        :status="staleBusChipStatus"
        :label="staleBusChipLabel"
        tooltip="Live bus geolocation records in Redis whose timestamp is older than 150 seconds. Only buses currently within their hours of operation count as stale; out-of-hours buses are ignored."
      />
    </div>

    <v-btn
      outline
      color="primary"
      :loading="apiInProgress"
      :disabled="apiInProgress"
      @click="detailDialog = true"
    >
      View all worker tasks
    </v-btn>

    <detail-dialog
      v-model="detailDialog"
      title="Map API Worker Tasks"
      :max-width="1200"
    >
      <v-data-table
        :headers="headers"
        :items="tasks"
        :loading="apiInProgress"
      >
        <template slot="items" slot-scope="props">
          <tr>
            <td>{{ props.item.label || props.item.name }}</td>
            <td>{{ props.item.lockState }}</td>
            <td>{{ props.item.nominalIntervalSeconds }}</td>
            <td>{{ props.item.lockTimeoutSeconds }}</td>
            <td>{{ props.item.queueDepth }}</td>
            <td>{{ props.item.status }}</td>
          </tr>
        </template>
      </v-data-table>
    </detail-dialog>
  </div>
</template>

<script>
import {
  getMapApiOverview,
  getMapApiRequestQueues,
  getMapApiWorkerTasks
} from '@/services/diagnostics-service'
import { addAlert } from '@/utilities/helpers'
import SystemHealthBanner from '../components/SystemHealthBanner.vue'
import StatusChip from '../components/StatusChip.vue'
import DetailDialog from '../components/DetailDialog.vue'
import { formatAgeShort } from '../components/status-mapping'

export default {
  name: 'realtime-diagnostics-map-api-tab',
  components: {
    SystemHealthBanner,
    StatusChip,
    DetailDialog
  },
  props: {
    refreshToken: {
      type: Number,
      default: 0
    },
    silentRefresh: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      apiInProgress: false,
      detailDialog: false,
      mapOverview: null,
      requestQueues: null,
      workerTasks: null,
      headers: [
        { text: 'Task', value: 'label' },
        { text: 'Lock State', value: 'lockState' },
        { text: 'Interval (s)', value: 'nominalIntervalSeconds' },
        { text: 'Lock Timeout (s)', value: 'lockTimeoutSeconds' },
        { text: 'Queue Depth', value: 'queueDepth' },
        { text: 'Status', value: 'status' }
      ]
    }
  },
  computed: {
    geoBackup () {
      return this.mapOverview && this.mapOverview.systems && this.mapOverview.systems.geoBackup
        ? this.mapOverview.systems.geoBackup
        : {}
    },
    geoBackupStatus () {
      return this.geoBackup.status || 'unknown'
    },
    geoBackupChipLabel () {
      if (this.geoBackup.sourceState === 'idle') {
        return 'Geo backup idle: no current geo data to persist'
      }

      return 'Geo backup: persisted ' + formatAgeShort(this.geoBackup.lastPersistedAgeSeconds) + ' ago • queue ' + (this.geoBackup.queueLength || 0)
    },
    bannerSubtitle () {
      if (this.geoBackup.sourceState === 'idle') {
        return 'No pending geo backup data. Last persisted backup: ' + formatAgeShort(this.geoBackup.lastPersistedAgeSeconds) + ' ago'
      }

      return 'Active geo data detected. Last persisted backup: ' + formatAgeShort(this.geoBackup.lastPersistedAgeSeconds) + ' ago'
    },
    queues () {
      return this.requestQueues && this.requestQueues.queues ? this.requestQueues.queues : []
    },
    counts () {
      return this.requestQueues && this.requestQueues.counts ? this.requestQueues.counts : {}
    },
    staleBusesCount () {
      return this.requestQueues ? Number(this.requestQueues.staleBusesCount || 0) : 0
    },
    transportationHashSummary () {
      return this.requestQueues && this.requestQueues.transportationHashSummary
        ? this.requestQueues.transportationHashSummary
        : {}
    },
    routeGeocodeChipStatus () {
      return this.getWorstQueueStatus(['routeRequests', 'geocodeRequests'])
    },
    routeGeocodeChipLabel () {
      return 'Route/geocode: ' + (this.counts.routeRequests || 0) + ' / ' + (this.counts.geocodeRequests || 0)
    },
    syncChipStatus () {
      return this.getQueueStatus('syncBusStopsRequests')
    },
    syncChipLabel () {
      return 'Bus stop sync: ' + (this.counts.syncBusStopsRequests || 0)
    },
    auditChipStatus () {
      const queue = this.findQueue('auditRouteFaresRequestStatus')
      return queue ? queue.status : 'unknown'
    },
    auditChipLabel () {
      const status = this.requestQueues ? this.requestQueues.auditRouteFaresStatus : null
      return 'Fare audit: ' + (status || 'idle')
    },
    distanceReportChipStatus () {
      return this.getQueueStatus('distanceTravelledReportRequests')
    },
    distanceReportChipLabel () {
      return 'Distance reports: ' + (this.counts.distanceTravelledReportRequests || 0)
    },
    cleanupChipStatus () {
      const cleanupCounts = [
        Number(this.counts.passengerHash || 0),
        Number(this.counts.transportationHash || 0),
        Number(this.counts.routeRequestResults || 0)
      ]
      const max = Math.max.apply(null, cleanupCounts)
      if (max > 1000) {
        return 'warning'
      }
      return 'healthy'
    },
    cleanupChipLabel () {
      return 'Cleanup hashes: p=' + (this.counts.passengerHash || 0) + ' t=' + (this.counts.transportationHash || 0)
    },
    staleBusChipStatus () {
      if (this.staleBusesCount > 0) {
        return 'warning'
      }
      return 'healthy'
    },
    staleBusChipLabel () {
      const outOfHoursCount = Number(this.transportationHashSummary.staleOutOfHoursCount || 0)
      const unknownHoursCount = Number(this.transportationHashSummary.staleUnknownHoursCount || 0)
      let label = 'Stale in-hours buses: ' + this.staleBusesCount

      if (outOfHoursCount || unknownHoursCount) {
        label += ' • ignored: ' + outOfHoursCount + ' out-of-hours'
      }

      if (unknownHoursCount) {
        label += ', ' + unknownHoursCount + ' unknown hours'
      }

      return label
    },
    tasks () {
      return this.workerTasks && this.workerTasks.tasks ? this.workerTasks.tasks : []
    }
  },
  watch: {
    refreshToken: {
      immediate: true,
      handler () {
        this.loadData(this.silentRefresh)
      }
    }
  },
  methods: {
    async loadData (silent) {
      if (!silent) {
        this.apiInProgress = true
      }

      try {
        const responses = await Promise.all([
          getMapApiOverview(),
          getMapApiRequestQueues(),
          getMapApiWorkerTasks()
        ])
        this.mapOverview = responses[0]
        this.requestQueues = responses[1]
        this.workerTasks = responses[2]
      } catch (err) {
        addAlert({
          type: 'error',
          message: err && err.message ? err.message : 'Failed to load map-api diagnostics'
        })
      } finally {
        if (!silent) {
          this.apiInProgress = false
        }
      }
    },
    findQueue (key) {
      return this.queues.find(function (queue) {
        return queue.key === key
      })
    },
    getQueueStatus (key) {
      const queue = this.findQueue(key)
      return queue ? queue.status : 'unknown'
    },
    getWorstQueueStatus (keys) {
      const statuses = (keys || []).map((key) => this.getQueueStatus(key))
      if (statuses.find((o) => o === 'critical')) {
        return 'critical'
      }
      if (statuses.find((o) => o === 'warning')) {
        return 'warning'
      }
      if (statuses.find((o) => o === 'stale')) {
        return 'stale'
      }
      if (statuses.find((o) => o === 'unknown')) {
        return 'unknown'
      }
      return 'healthy'
    }
  }
}
</script>
